import { useState, useRef, useEffect } from 'react';
import { useTenant } from '../contexts/TenantContext.tsx';
import { useCart } from '../contexts/CartContext.tsx';
import { ItemSheet } from '../components/ItemSheet.tsx';
import { CartBar } from '../components/CartBar.tsx';
import { formatMoney } from '../lib/format.ts';
import type { MenuItem } from '../lib/types.ts';

export function MenuPage() {
  const { snapshot, slug } = useTenant();
  const { categories, tenant, location } = snapshot;
  const [activeCategory, setActiveCategory] = useState(categories[0]?.local_id ?? '');
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);
  const categoryRefs = useRef<Record<string, HTMLElement | null>>({});
  const navRef = useRef<HTMLDivElement>(null);
  const scrollingProgrammatically = useRef(false);

  useEffect(() => {
    const ids = categories.map((c) => c.local_id);
    const observer = new IntersectionObserver(
      (entries) => {
        if (scrollingProgrammatically.current) return;
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveCategory(entry.target.id.replace('cat-', ''));
            break;
          }
        }
      },
      { rootMargin: '-60px 0px -70% 0px', threshold: 0 },
    );
    for (const id of ids) {
      const el = categoryRefs.current[id];
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [categories]);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const activePill = nav.querySelector(`[data-cat="${activeCategory}"]`) as HTMLElement | null;
    if (activePill) {
      activePill.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [activeCategory]);

  function scrollToCategory(localId: string) {
    const el = categoryRefs.current[localId];
    if (!el) return;
    scrollingProgrammatically.current = true;
    setActiveCategory(localId);
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setTimeout(() => { scrollingProgrammatically.current = false; }, 800);
  }

  const accent = tenant.theme.accent;

  return (
    <div className="flex min-h-dvh flex-col" style={{ background: 'var(--bg)', paddingBottom: 88 }}>

      {/* Header */}
      <header
        className="sticky top-0 z-20 px-4 py-3"
        style={{ background: 'var(--surface)', boxShadow: '0 1px 0 var(--border)' }}
      >
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <h1 className="truncate font-bold text-base tracking-tight" style={{ color: 'var(--text)' }}>
              {tenant.name}
            </h1>
            <p className="truncate text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
              {location.address}
            </p>
          </div>
          {tenant.stage === 'demo' && (
            <span
              className="flex-shrink-0 rounded-full px-2.5 py-1 text-xs font-600 tracking-wide"
              style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}
            >
              Демо
            </span>
          )}
        </div>
      </header>

      {/* Category nav */}
      <div
        ref={navRef}
        role="tablist"
        aria-label="Категории меню"
        className="sticky top-[52px] z-10 flex gap-2 overflow-x-auto px-4 py-2.5 scrollbar-none"
        style={{ background: 'var(--surface)', boxShadow: '0 1px 0 var(--border)' }}
      >
        {categories.map((cat) => {
          const isActive = cat.local_id === activeCategory;
          return (
            <button
              key={cat.local_id}
              role="tab"
              aria-selected={isActive}
              data-cat={cat.local_id}
              onClick={() => scrollToCategory(cat.local_id)}
              className="flex-shrink-0 rounded-full px-4 text-sm font-600 transition-all duration-150"
              style={{
                background: isActive ? accent : 'var(--n-100)',
                color: isActive ? '#fff' : 'var(--text-muted)',
                minHeight: 36,
                letterSpacing: isActive ? '0.01em' : undefined,
              }}
            >
              {cat.name}
            </button>
          );
        })}
      </div>

      {/* Menu sections */}
      <main className="flex-1 px-3 pt-5">
        {categories.map((cat) => (
          <section
            key={cat.local_id}
            id={`cat-${cat.local_id}`}
            ref={(el) => { categoryRefs.current[cat.local_id] = el; }}
            className="mb-8"
          >
            <h2 className="mb-1 text-xl font-700 tracking-tight" style={{ color: 'var(--text)' }}>
              {cat.name}
            </h2>
            {cat.description && (
              <p className="mb-3 text-sm" style={{ color: 'var(--text-muted)' }}>
                {cat.description}
              </p>
            )}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {cat.items.map((item) => (
                <MenuItemCard
                  key={item.item_id}
                  item={item}
                  currency={tenant.currency}
                  accent={accent}
                  onOpen={() => setSelectedItem(item)}
                />
              ))}
            </div>
          </section>
        ))}
      </main>

      {selectedItem && (
        <ItemSheet
          item={selectedItem}
          currency={tenant.currency}
          accentColor={accent}
          onClose={() => setSelectedItem(null)}
        />
      )}

      <CartBar slug={slug} accentColor={accent} />
    </div>
  );
}

interface MenuItemCardProps {
  item: MenuItem;
  currency: 'RUB' | 'KZT';
  accent: string;
  onOpen: () => void;
}

function MenuItemCard({ item, currency, accent, onOpen }: MenuItemCardProps) {
  const { lines, addLine } = useCart();
  const inCart = lines
    .filter((l) => l.item_local_id === item.item_local_id)
    .reduce((sum, l) => sum + l.quantity, 0);

  const unavailable = item.stop_list || !item.is_available;

  function handleAdd(e: React.MouseEvent) {
    e.stopPropagation();
    if (unavailable) return;
    if (item.modifier_groups && item.modifier_groups.length > 0) {
      onOpen();
    } else {
      addLine({
        line_id: crypto.randomUUID(),
        item_local_id: item.item_local_id,
        item_name: item.name,
        image_path: item.image_path,
        quantity: 1,
        selected: [],
        estimated_unit_price: item.price,
        estimated_load: item.load_units,
      });
    }
  }

  return (
    <article
      className="relative flex flex-col overflow-hidden rounded-xl transition-transform duration-150 active:scale-[0.97]"
      style={{
        background: 'var(--surface)',
        boxShadow: '0 1px 3px oklch(0% 0 0 / 0.08), 0 0 0 1px var(--border)',
        opacity: unavailable ? 0.6 : 1,
      }}
    >
      {/* Tappable area — image + info */}
      <button
        onClick={unavailable ? undefined : onOpen}
        disabled={unavailable}
        aria-label={`${item.name}${unavailable ? ', недоступно' : ', открыть'}`}
        className="flex flex-1 flex-col text-left focus-visible:outline-none"
        style={{ cursor: unavailable ? 'not-allowed' : 'pointer' }}
      >
        {/* Image — fixed 4:3 aspect */}
        <div className="relative w-full" style={{ paddingBottom: '75%' }}>
          {item.image_path ? (
            <img
              src={`/tenants/${item.image_path}`}
              alt={item.name}
              loading="lazy"
              decoding="async"
              className="absolute inset-0 h-full w-full object-cover"
            />
          ) : (
            <div
              className="absolute inset-0 flex items-center justify-center text-3xl"
              style={{ background: 'var(--n-100)' }}
              aria-hidden="true"
            >
              🌯
            </div>
          )}

          {/* Cart badge */}
          {inCart > 0 && (
            <span
              className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full text-xs font-700"
              style={{ background: accent, color: '#fff', boxShadow: '0 2px 6px oklch(0% 0 0 / 0.25)' }}
            >
              {inCart}
            </span>
          )}

          {/* Unavailable overlay */}
          {unavailable && (
            <div className="absolute inset-0 flex items-end p-2" style={{ background: 'oklch(0% 0 0 / 0.35)' }}>
              <span
                className="rounded-md px-2 py-0.5 text-xs font-600 text-white"
                style={{ background: 'oklch(0% 0 0 / 0.65)' }}
              >
                {item.stop_list ? 'Стоп-лист' : 'Недоступно'}
              </span>
            </div>
          )}
        </div>

        {/* Text info */}
        <div className="flex flex-1 flex-col gap-1 px-2.5 pt-2 pb-10">
          <p className="text-sm font-600 leading-snug" style={{ color: 'var(--text)' }}>
            {item.name}
          </p>
          {item.description && (
            <p className="line-clamp-2 text-xs leading-relaxed" style={{ color: 'var(--text-muted)' }}>
              {item.description}
            </p>
          )}
          <p className="mt-auto pt-1 text-sm font-700 tabular-nums" style={{ color: 'var(--text)' }}>
            {formatMoney(item.price, currency)}
          </p>
        </div>
      </button>

      {/* Add button — bottom-right, always visible */}
      {!unavailable && (
        <button
          onClick={handleAdd}
          aria-label={`Добавить ${item.name}`}
          className="absolute bottom-2 right-2 flex h-8 w-8 items-center justify-center rounded-full text-white transition-transform duration-150 active:scale-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
          style={{
            background: accent,
            boxShadow: '0 2px 8px oklch(0% 0 0 / 0.20)',
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ['--tw-ring-color' as any]: accent,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
      )}
    </article>
  );
}
